import React, { useState, useEffect } from 'react';
import { Wand2, Copy, CheckCircle, FileText, Sparkles, ChevronDown, Settings, Zap, Info, ArrowRight, Lightbulb, Target, Brain, MessageSquare, Palette, Search } from 'lucide-react';

const PromptGenerator = () => {
  const [promptType, setPromptType] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [generatedPrompt, setGeneratedPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [categorySearch, setCategorySearch] = useState('');
  
  // Mode édition assistée
  const [editMode, setEditMode] = useState(false);
  const [bracketFields, setBracketFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [currentFieldIndex, setCurrentFieldIndex] = useState(0);
  const [finalPrompt, setFinalPrompt] = useState('');

  // Options simplifiées - seulement le niveau
  const [targetAudience, setTargetAudience] = useState('debutant');

  // Catégories avec emojis et descriptions
  const allCategories = {
    pro: [
      // Business & Commerce
      { value: 'strategie', label: '🎯 Stratégie & Business', domain: 'business', description: 'Plans stratégiques, analyses de marché' },
      { value: 'marketing', label: '📢 Marketing & Communication', domain: 'business', description: 'Campagnes, contenu, branding' },
      { value: 'vente', label: '💼 Vente & Commercial', domain: 'business', description: 'Techniques de vente, négociation' },
      { value: 'finance', label: '💰 Finance & Comptabilité', domain: 'business', description: 'Analyses financières, budgets' },
      { value: 'rh', label: '👥 Ressources Humaines', domain: 'business', description: 'Recrutement, formation, culture' },
      { value: 'juridique', label: '⚖️ Juridique & Légal', domain: 'business', description: 'Contrats, conformité, conseil' },
      { value: 'ecommerce', label: '🛒 E-commerce & Retail', domain: 'business', description: 'Vente en ligne, expérience client' },
      { value: 'immobilier', label: '🏠 Immobilier & Property', domain: 'business', description: 'Investissement, gestion, vente' },
      
      // Tech & Innovation
      { value: 'developpement_enterprise', label: '💻 Développement Entreprise', domain: 'tech', description: 'Solutions logicielles, architecture' },
      { value: 'ia_business', label: '🤖 IA & Business Intelligence', domain: 'tech', description: 'Machine learning, analytics' },
      { value: 'data_analytics', label: '📊 Data Science & Analytics', domain: 'tech', description: 'Analyse de données, insights' },
      { value: 'cybersecurite', label: '🔒 Cybersécurité', domain: 'tech', description: 'Protection, audit, conformité' },
      { value: 'devops_enterprise', label: '⚙️ DevOps & Infrastructure', domain: 'tech', description: 'CI/CD, cloud, automation' },
      { value: 'blockchain', label: '🔗 Blockchain & Web3', domain: 'tech', description: 'Crypto, smart contracts, DeFi' },
      
      // Industrie & Production
      { value: 'ingenierie', label: '🏭 Ingénierie & Manufacturing', domain: 'industry', description: 'Production, qualité, R&D' },
      { value: 'logistique', label: '🚛 Logistique & Supply Chain', domain: 'industry', description: 'Transport, stocks, optimisation' },
      { value: 'energie', label: '⚡ Énergie & Utilities', domain: 'industry', description: 'Renouvelables, efficacité' },
      { value: 'construction', label: '🏗️ Construction & BTP', domain: 'industry', description: 'Projets, gestion, sécurité' },
      { value: 'agriculture', label: '🌾 Agriculture & AgroTech', domain: 'industry', description: 'Innovation agricole, durabilité' },
      
      // Management & Conseil
      { value: 'gestion_projet', label: '📋 Gestion de Projet', domain: 'management', description: 'Méthodologies, planification' },
      { value: 'consulting', label: '💡 Conseil & Audit', domain: 'management', description: 'Analyses, recommandations' },
      { value: 'transformation_digitale', label: '🚀 Transformation Digitale', domain: 'management', description: 'Innovation, changement' },
      { value: 'leadership', label: '👔 Leadership & Management', domain: 'management', description: 'Direction, équipes, culture' },
      { value: 'formation_pro', label: '🎓 Formation Professionnelle', domain: 'management', description: 'Upskilling, programmes' }
    ],
    creatif: [
      // Création & Design
      { value: 'design_graphique', label: '🎨 Design Graphique', domain: 'creative', description: 'Logos, visuels, identité' },
      { value: 'ux_ui_design', label: '📱 UX/UI Design', domain: 'creative', description: 'Interfaces, expérience utilisateur' },
      { value: 'illustration', label: '🖼️ Illustration & Art Digital', domain: 'creative', description: 'Dessins, art numérique' },
      { value: 'video_creative', label: '🎬 Vidéo & Motion', domain: 'creative', description: 'Montage, animation, storytelling' },
      { value: 'photographie', label: '📷 Photographie', domain: 'creative', description: 'Shooting, retouche, style' },
      { value: '3d_design', label: '🎮 3D & Animation', domain: 'creative', description: 'Modélisation, rendu, VFX' },
      { value: 'branding_personal', label: '✨ Branding Personnel', domain: 'creative', description: 'Image de marque, positionnement' },
      
      // Contenu & Communication
      { value: 'redaction_creative', label: '✍️ Rédaction Créative', domain: 'content', description: 'Articles, stories, contenus' },
      { value: 'storytelling', label: '📚 Storytelling & Narratif', domain: 'content', description: 'Histoires, scénarios' },
      { value: 'social_media', label: '📱 Réseaux Sociaux', domain: 'content', description: 'Posts, engagement, communauté' },
      { value: 'copywriting', label: '📝 Copywriting', domain: 'content', description: 'Textes persuasifs, accroches' },
      { value: 'podcasting', label: '🎙️ Podcasting & Audio', domain: 'content', description: 'Émissions, montage audio' },
      { value: 'blogging', label: '💭 Blogging & Newsletter', domain: 'content', description: 'Articles de blog, infolettres' },
      { value: 'youtube_creation', label: '📹 YouTube & Streaming', domain: 'content', description: 'Vidéos, lives, monétisation' },
      
      // Développement Personnel
      { value: 'coaching_personnel', label: '🌟 Développement Personnel', domain: 'development', description: 'Coaching, motivation, objectifs' },
      { value: 'langues_perso', label: '🌍 Langues & Communication', domain: 'development', description: 'Apprentissage, pratique' },
      { value: 'productivite_perso', label: '⚡ Productivité', domain: 'development', description: 'Organisation, méthodes, outils' },
      { value: 'mindfulness', label: '🧘 Bien-être & Mindfulness', domain: 'development', description: 'Méditation, équilibre' },
      { value: 'carriere', label: '🚀 Évolution de Carrière', domain: 'development', description: 'CV, entretiens, reconversion' },
      
      // Lifestyle & Loisirs
      { value: 'mode_style', label: '👗 Mode & Style', domain: 'lifestyle', description: 'Tendances, looks, conseils' },
      { value: 'cuisine_food', label: '🍳 Cuisine & Food', domain: 'lifestyle', description: 'Recettes, nutrition, tendances' },
      { value: 'voyage_expatriation', label: '✈️ Voyage & Expatriation', domain: 'lifestyle', description: 'Destinations, conseils, vie à l\'étranger' },
      { value: 'sport_fitness', label: '🏃 Sport & Fitness', domain: 'lifestyle', description: 'Entraînement, nutrition sportive' },
      { value: 'decoration', label: '🏡 Décoration & Home', domain: 'lifestyle', description: 'Intérieur, DIY, aménagement' },
      { value: 'parenting', label: '👶 Parentalité & Famille', domain: 'lifestyle', description: 'Éducation, activités, conseils' },
      { value: 'animaux', label: '🐾 Animaux & Pets', domain: 'lifestyle', description: 'Soins, dressage, bien-être' },
      
      // Entertainment & Culture
      { value: 'gaming_creative', label: '🎮 Gaming & Esports', domain: 'entertainment', description: 'Jeux, streaming, communauté' },
      { value: 'musique', label: '🎵 Musique & Audio', domain: 'entertainment', description: 'Création, production, MAO' },
      { value: 'cinema_series', label: '🎬 Cinéma & Séries', domain: 'entertainment', description: 'Critiques, analyses, création' },
      { value: 'litterature', label: '📖 Littérature & Écriture', domain: 'entertainment', description: 'Romans, poésie, édition' },
      { value: 'art_culture', label: '🎭 Art & Culture', domain: 'entertainment', description: 'Expositions, critiques, création' },
      
      // Tech Créatif
      { value: 'web_design', label: '💻 Web Design', domain: 'tech_creative', description: 'Sites web, landing pages' },
      { value: 'app_design', label: '📲 App Design', domain: 'tech_creative', description: 'Applications mobiles, UX' },
      { value: 'nocode', label: '🔧 No-Code & Automation', domain: 'tech_creative', description: 'Outils, workflows, zapier' }
    ]
  };

  const promptTypes = [
    {
      id: 'pro',
      title: 'Professionnel',
      subtitle: 'Business & Entreprise',
      icon: Target,
      description: 'Pour des objectifs business et ROI',
      color: 'blue'
    },
    {
      id: 'creatif',
      title: 'Créatif',
      subtitle: 'Personnel & Lifestyle',
      icon: Palette,
      description: 'Pour créer et inspirer',
      color: 'purple'
    }
  ];

  const audienceOptions = [
    { value: 'debutant', label: '🌱 Débutant', description: 'Explications simples et guidées' },
    { value: 'intermediaire', label: '🌿 Intermédiaire', description: 'Approche équilibrée' },
    { value: 'expert', label: '🌳 Expert', description: 'Techniques avancées' }
  ];

  // Exemples enrichis avec plus de contexte
  const exampleSuggestions = {
    pro: {
      strategie: [
        { 
          title: "Croissance d'entreprise",
          text: "Créer un plan de croissance pour doubler le CA en 2 ans",
          context: "Budget: 100K€, Équipe: 5 personnes, Secteur: SaaS B2B"
        },
        { 
          title: "Expansion internationale",
          text: "Développer une stratégie d'expansion en Europe",
          context: "Marchés cibles: Allemagne et Espagne, Timeline: 18 mois"
        },
        { 
          title: "Analyse concurrentielle",
          text: "Analyser la concurrence et identifier 3 opportunités de différenciation",
          context: "Top 5 concurrents directs, Focus sur l'innovation produit"
        }
      ],
      marketing: [
        { 
          title: "Campagne de notoriété",
          text: "Lancer une campagne digitale avec un budget de 50K€",
          context: "Cible: PME 20-100 employés, Durée: 3 mois, KPI: +30% notoriété"
        },
        { 
          title: "Génération de leads B2B",
          text: "Créer une stratégie de contenu pour générer 200 leads qualifiés/mois",
          context: "Content marketing + LinkedIn, Budget: 10K€/mois"
        },
        { 
          title: "Lancement produit",
          text: "Plan marketing complet pour lancer un nouveau produit tech",
          context: "Go-to-market en 60 jours, Budget: 80K€, Cible: Startups"
        }
      ],
      vente: [
        { 
          title: "Optimisation commerciale",
          text: "Réduire le cycle de vente de 90 à 60 jours",
          context: "Équipe: 10 commerciaux, CRM: Salesforce, Vente complexe B2B"
        },
        { 
          title: "Formation équipe",
          text: "Former l'équipe aux techniques de vente consultative",
          context: "20 commerciaux, 3 jours de formation, Focus grands comptes"
        },
        { 
          title: "Argumentaire SaaS",
          text: "Créer un pitch deck pour solution SaaS entreprise",
          context: "Prix: 50K€/an, Cible: DSI grandes entreprises, ROI en 6 mois"
        }
      ]
    },
    creatif: {
      design_graphique: [
        { 
          title: "Identité startup",
          text: "Créer une identité visuelle complète pour une startup tech",
          context: "Logo, charte graphique, déclinaisons web et print"
        },
        { 
          title: "Campagne visuelle",
          text: "Designer 10 affiches pour un festival de musique",
          context: "Style moderne et coloré, Format A2, Livraison en 2 semaines"
        },
        { 
          title: "Design system",
          text: "Développer un système de design cohérent pour une app mobile",
          context: "Components UI, guidelines, documentation Figma"
        }
      ],
      social_media: [
        { 
          title: "Instagram écolo",
          text: "30 posts Instagram pour promouvoir un lifestyle éco-responsable",
          context: "3 posts/semaine, Mix photos/carrousels/reels, Ton inspirant"
        },
        { 
          title: "Stratégie TikTok",
          text: "Lancer et développer un compte TikTok pour toucher la Gen Z",
          context: "Objectif: 10K followers en 3 mois, Niche: Tech & Gaming"
        },
        { 
          title: "Community management",
          text: "Animer une communauté LinkedIn de 5000 professionnels RH",
          context: "5 posts/semaine, Engagement rate > 5%, Contenu expert"
        }
      ],
      voyage_expatriation: [
        { 
          title: "Expatriation Canada",
          text: "Guide complet pour s'expatrier au Canada en famille",
          context: "Visa, logement, école, santé, budget 1ère année"
        },
        { 
          title: "Tour d'Asie",
          text: "Itinéraire 3 mois en Asie du Sud-Est avec 5000€",
          context: "Solo, backpack, 6 pays, focus expériences locales"
        },
        { 
          title: "Blog voyage",
          text: "Créer et monétiser un blog de voyage en 6 mois",
          context: "SEO, affiliations, partenariats, objectif 2K€/mois"
        }
      ]
    }
  };

  // Suggestions contextuelles pour l'édition assistée
  const fieldSuggestions = {
    pro: {
      "[Votre domaine de spécialisation]": [
        "Marketing digital et growth hacking",
        "Stratégie d'entreprise et transformation",
        "Finance d'entreprise et contrôle de gestion",
        "Gestion de projet agile et Scrum",
        "Ressources humaines et talent management"
      ],
      "[X années]": [
        "5 années",
        "10 années", 
        "15 années",
        "Plus de 20 années"
      ],
      "[Certifications/Reconnaissances]": [
        "MBA, Certification PMP",
        "Certification Google Ads et Analytics",
        "Certification Scrum Master",
        "Expert-comptable diplômé",
        "Certification ITIL et Prince2"
      ],
      "[Frameworks reconnus]": [
        "Lean Six Sigma, Agile, Design Thinking",
        "SWOT, Porter, BCG Matrix",
        "OKR, KPI, Balanced Scorecard",
        "PDCA, Kaizen, 5S",
        "Business Model Canvas, Value Proposition"
      ],
      "[Architecture système/organisationnelle]": [
        "Structure matricielle, 5 BU, 500 collaborateurs",
        "Organisation agile en squads autonomes",
        "Architecture microservices sur AWS",
        "Système ERP SAP intégré",
        "Environnement multi-sites international"
      ],
      "[KPIs et OKRs détaillés]": [
        "CA: +20%, Marge: +5pts, NPS > 50",
        "Réduction coûts: -15%, ROI: 300%",
        "Time to Market: -30%, Qualité: +25%",
        "Taux conversion: 5%, CAC < 100€",
        "Productivité: +40%, Turn-over < 5%"
      ]
    },
    creatif: {
      "[Votre domaine de spécialisation]": [
        "Design graphique et branding",
        "Création de contenu et storytelling",
        "Photographie et direction artistique",
        "Développement web créatif",
        "Marketing d'influence et réseaux sociaux"
      ],
      "[Domaine où vous commencez]": [
        "la création de contenu Instagram",
        "le design graphique",
        "la photographie professionnelle",
        "l'écriture créative",
        "le montage vidéo"
      ],
      "[Ce que vous voulez accomplir simplement]": [
        "créer ma première identité visuelle complète",
        "lancer mon blog et avoir 1000 visiteurs/mois",
        "développer ma présence sur les réseaux sociaux",
        "créer mon portfolio professionnel",
        "monétiser ma passion créative"
      ],
      "[Temps/Budget/Connaissances limitées]": [
        "3 mois, budget 500€, débutant complet",
        "6 mois, budget 1000€, notions de base",
        "Temps libre le soir, budget minimal",
        "2h/jour, budget 200€/mois",
        "Week-ends disponibles, sans budget"
      ],
      "[Difficultés principales]": [
        "Je ne sais pas par où commencer",
        "Je manque de confiance en mes créations",
        "Je n'ai pas les bons outils",
        "Je ne comprends pas la technique",
        "Je n'arrive pas à être régulier"
      ]
    }
  };

  // Fonction pour extraire les champs entre crochets
  const extractBracketFields = (text) => {
    const regex = /\[([^\]]+)\]/g;
    const matches = [];
    let match;
    
    while ((match = regex.exec(text)) !== null) {
      matches.push({
        fullMatch: match[0],
        content: match[1],
        startIndex: match.index,
        endIndex: match.index + match[0].length
      });
    }
    
    return matches;
  };

  // Fonction pour remplacer les valeurs dans le prompt
  const replaceBracketValues = (prompt, values) => {
    let finalText = prompt;
    
    Object.keys(values).forEach(key => {
      if (values[key]) {
        finalText = finalText.replace(key, values[key]);
      }
    });
    
    return finalText;
  };

  // Fonction pour obtenir le prompt avec aperçu en temps réel
  const getPreviewPrompt = () => {
    if (!generatedPrompt) return '';
    
    let previewText = generatedPrompt;
    Object.keys(fieldValues).forEach(key => {
      if (fieldValues[key]) {
        previewText = previewText.replace(key, fieldValues[key]);
      }
    });
    
    return previewText;
  };

  // Démarrer l'édition assistée
  const startAssistedEditing = () => {
    const fields = extractBracketFields(generatedPrompt);
    setBracketFields(fields);
    setFieldValues({});
    setCurrentFieldIndex(0);
    setEditMode(true);
    setFinalPrompt('');
  };

  // Passer au champ suivant
  const nextField = () => {
    if (currentFieldIndex < bracketFields.length - 1) {
      setCurrentFieldIndex(currentFieldIndex + 1);
    }
  };

  // Revenir au champ précédent
  const previousField = () => {
    if (currentFieldIndex > 0) {
      setCurrentFieldIndex(currentFieldIndex - 1);
    }
  };

  // Terminer l'édition et générer le prompt final
  const finishEditing = () => {
    const final = replaceBracketValues(generatedPrompt, fieldValues);
    setFinalPrompt(final);
  };

  // Obtenir les suggestions pour le champ actuel
  const getCurrentFieldSuggestions = () => {
    if (!bracketFields[currentFieldIndex]) return [];
    
    const currentField = bracketFields[currentFieldIndex].fullMatch;
    const suggestions = fieldSuggestions[promptType]?.[currentField] || [];
    
    // Suggestions génériques si pas de suggestions spécifiques
    if (suggestions.length === 0) {
      if (currentField.toLowerCase().includes('budget')) {
        return ['1000€', '5000€', '10000€', '50000€', 'À définir'];
      } else if (currentField.toLowerCase().includes('délai') || currentField.toLowerCase().includes('temps')) {
        return ['1 mois', '3 mois', '6 mois', '1 an', 'Flexible'];
      } else if (currentField.toLowerCase().includes('format')) {
        return ['PDF', 'Présentation', 'Document Word', 'Rapport détaillé', 'Guide pratique'];
      }
    }
    
    return suggestions;
  };

  // Mise à jour du step actuel
  useEffect(() => {
    if (promptType && !selectedCategory) setCurrentStep(2);
    else if (selectedCategory && !description) setCurrentStep(3);
    else if (description) setCurrentStep(4);
    else setCurrentStep(1);
  }, [promptType, selectedCategory, description]);

  const handleGenerate = async () => {
    if (!description.trim() || !selectedCategory) return;
    
    setIsGenerating(true);
    
    setTimeout(() => {
      const autoTitle = title || `Prompt ${promptType === 'pro' ? 'Professionnel' : 'Créatif'} - ${allCategories[promptType].find(cat => cat.value === selectedCategory)?.label || 'Spécialisé'}`;
      
      // Technique automatique selon le niveau
      let engineeringSection = '';
      
      if (targetAudience === 'debutant') {
        engineeringSection = `
## 🎯 APPROCHE ADAPTÉE : Simple et Guidée

Je vais t'accompagner pas à pas avec :
- Des explications claires et accessibles
- Des exemples concrets pour chaque étape  
- Un langage simple sans jargon technique
- Des encouragements et validations régulières

Ma réponse sera structurée en étapes numérotées faciles à suivre.`;
      } else if (targetAudience === 'intermediaire') {
        engineeringSection = `
## 📋 APPROCHE ADAPTÉE : Structurée et Pratique

Ma méthodologie :
1. **Analyse** de ton contexte et tes objectifs
2. **Proposition** de 2-3 solutions adaptées avec pros/cons
3. **Recommandation** argumentée de la meilleure approche
4. **Plan d'action** détaillé et mesurable

Équilibre entre concepts théoriques et application pratique.`;
      } else {
        engineeringSection = `
## 🚀 APPROCHE ADAPTÉE : Expertise Avancée

Framework d'analyse :
- **Diagnostic systémique** avec identification des leviers critiques
- **Modélisation** de scénarios avec projections quantifiées
- **Stratégie optimale** basée sur les best practices sectorielles
- **KPIs et métriques** pour tracking et optimisation continue
- **Risk assessment** et plans de mitigation

Utilisation de méthodologies avancées et données benchmark.`;
      }

      const currentCategoryLabel = allCategories[promptType].find(cat => cat.value === selectedCategory)?.label || selectedCategory;

      const adaptedTone = targetAudience === 'debutant' ? 
        'accessible et encourageant' : 
        targetAudience === 'intermediaire' ? 
        'professionnel et pédagogique' : 
        'expert et analytique';

      const contextSection = targetAudience === 'debutant' ? `
## 🌱 CONTEXTE SIMPLIFIÉ
**Mon niveau :** Je débute dans ce domaine
**Mon besoin :** ${description}
**Mes attentes :** Une solution simple à mettre en œuvre
**Format souhaité :** Guide étape par étape avec exemples` :
      targetAudience === 'intermediaire' ? `
## 🌿 CONTEXTE PROFESSIONNEL  
**Ma situation :** ${description}
**Niveau d'expertise :** Connaissances intermédiaires à consolider
**Résultats attendus :** Solution professionnelle et applicable
**Contraintes :** À préciser selon votre contexte spécifique` : `
## 🌳 CONTEXTE STRATÉGIQUE
**Challenge :** ${description}
**Niveau d'exigence :** Excellence opérationnelle et innovation
**Objectifs mesurables :** ROI, KPIs et impacts business
**Environnement :** Complexité élevée nécessitant une approche systémique`;

      const mockPrompt = `# ${autoTitle}

## 🎯 RÔLE & EXPERTISE
Tu es un **expert ${targetAudience === 'debutant' ? 'bienveillant' : 'reconnu'} en ${currentCategoryLabel?.replace(/^[🎯📢💼💰👥⚖️🛒💻🤖📊🔒⚙️🏭🚛⚡🏗️📋💼🚀🎨📱🖼️🎬✨✍️📚📱📝🎙️🌟🌍⚡👗🍳✈️🏃🎮🎵💻]\s/, '')}** avec une approche **${adaptedTone}**.

Tu maîtrises parfaitement :
${promptType === 'pro' ? 
`• Les enjeux business et stratégiques du secteur
• Les méthodologies et frameworks reconnus
• Les meilleures pratiques et innovations
• L'optimisation des processus et du ROI` :
`• Les tendances créatives et culturelles actuelles
• Les techniques artistiques et narratives
• Les outils et plateformes modernes
• L'engagement et l'impact émotionnel`}
${engineeringSection}
${contextSection}

## 📌 INSTRUCTIONS CLÉS

${targetAudience === 'debutant' ? 
`1. **Commence par valider** que tu as bien compris ma demande
2. **Explique simplement** chaque concept nouveau
3. **Donne des exemples** concrets et parlants
4. **Guide-moi pas à pas** avec des étapes claires
5. **Rassure et encourage** tout au long du processus` :
targetAudience === 'intermediaire' ?
`1. **Analyse d'abord** le contexte et les enjeux clés
2. **Propose plusieurs options** avec avantages/inconvénients  
3. **Recommande** la meilleure approche en justifiant
4. **Détaille** un plan d'action concret et mesurable
5. **Anticipe** les difficultés potentielles et solutions` :
`1. **Diagnostic approfondi** de la situation avec data
2. **Benchmark** des meilleures pratiques sectorielles
3. **Stratégie différenciante** avec avantage compétitif
4. **Roadmap détaillée** avec milestones et KPIs
5. **ROI projeté** et métriques de succès`}

## ✨ LIVRABLE ATTENDU

Fournis une réponse **complète et actionnable** qui :
- Répond précisément à mon besoin
- Est adaptée à mon niveau ${targetAudience}
- Peut être mise en œuvre immédiatement
- ${targetAudience === 'expert' ? 'Inclut des métriques et projections chiffrées' : 'Reste simple et accessible'}

**Format :** ${promptType === 'pro' ? '[Plan structuré / Stratégie / Analyse]' : '[Guide pratique / Tutoriel / Création]'}

---

*Commence maintenant en t'assurant de bien comprendre ma demande et en adaptant ta réponse à mon niveau ${targetAudience}.*`;

      setGeneratedPrompt(mockPrompt);
      setIsGenerating(false);
    }, 1500);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedPrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const formatPromptWithHighlights = (text, isPreview = false) => {
    const parts = text.split(/(\[.*?\])/g);
    return parts.map((part, index) => {
      if (part.startsWith('[') && part.endsWith(']')) {
        const value = fieldValues[part];
        if (isPreview && value) {
          return (
            <span key={index} className="bg-green-100 text-green-700 px-1 rounded font-medium">
              {value}
            </span>
          );
        }
        return (
          <span key={index} className="bg-blue-100 text-blue-700 px-1 rounded font-medium transition-all hover:bg-blue-200">
            {part}
          </span>
        );
      }
      return part;
    });
  };

  const applySuggestion = (suggestion) => {
    setDescription(suggestion.text + "\n\nContexte : " + suggestion.context);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header amélioré */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-3xl mb-4 shadow-lg animate-pulse">
            <Wand2 className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            PromptHub<span className="text-indigo-600">.fr</span>
          </h1>
          <p className="text-gray-600 text-xl max-w-2xl mx-auto">
            Créez des prompts experts en 3 clics, adaptés à votre niveau
          </p>
          
          {/* Progress indicator */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all ${
                  currentStep >= step 
                    ? 'bg-indigo-600 text-white scale-110' 
                    : 'bg-gray-200 text-gray-400'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`w-20 h-1 mx-2 transition-all ${
                    currentStep > step ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Configuration Panel - Plus large */}
          <div className="lg:col-span-2 space-y-6">
            {/* Niveau d'audience en premier pour adapter l'expérience */}
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 transition-all hover:shadow-xl">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="w-6 h-6 text-indigo-600" />
                <h3 className="text-lg font-semibold text-gray-900">Quel est votre niveau ?</h3>
                <div className="ml-auto">
                  <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    Adapte automatiquement la complexité
                  </span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {audienceOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setTargetAudience(option.value)}
                    className={`p-4 rounded-xl border-2 transition-all transform hover:scale-105 ${
                      targetAudience === option.value
                        ? 'border-indigo-500 bg-indigo-50 shadow-md'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="text-2xl mb-2">{option.label.split(' ')[0]}</div>
                    <div className={`font-medium text-sm ${
                      targetAudience === option.value ? 'text-indigo-900' : 'text-gray-900'
                    }`}>
                      {option.label.split(' ')[1]}
                    </div>
                    <div className={`text-xs mt-1 ${
                      targetAudience === option.value ? 'text-indigo-600' : 'text-gray-500'
                    }`}>
                      {option.description}
                    </div>
                  </button>
                ))}
              </div>
              {/* Info sur l'adaptation automatique */}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="text-sm text-blue-700 flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <strong>Technique automatique :</strong> La méthode de raisonnement s'adapte automatiquement à votre niveau pour une expérience optimale.
                  </div>
                </div>
              </div>
            </div>

            {/* Étape 1: Type de Prompt */}
            <div className={`bg-white rounded-2xl shadow-lg border p-6 transition-all ${
              currentStep >= 1 ? 'border-indigo-200 shadow-xl' : 'border-gray-100 opacity-90'
            }`}>
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                  currentStep >= 1 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                }`}>
                  1
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Choisissez votre objectif
                </h3>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {promptTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <button
                      key={type.id}
                      onClick={() => {
                        setPromptType(type.id);
                        setSelectedCategory('');
                        setDescription('');
                      }}
                      className={`p-6 rounded-xl border-2 transition-all transform hover:scale-105 ${
                        promptType === type.id
                          ? `border-${type.color}-500 bg-${type.color}-50 shadow-lg`
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <IconComponent className={`w-8 h-8 mx-auto mb-3 ${
                        promptType === type.id ? `text-${type.color}-600` : 'text-gray-400'
                      }`} />
                      <div className={`font-semibold text-lg mb-1 ${
                        promptType === type.id ? `text-${type.color}-900` : 'text-gray-900'
                      }`}>
                        {type.title}
                      </div>
                      <div className={`text-sm ${
                        promptType === type.id ? `text-${type.color}-600` : 'text-gray-500'
                      }`}>
                        {type.description}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Étape 2: Domaine */}
            {promptType && (
              <div className={`bg-white rounded-2xl shadow-lg border p-6 transition-all animate-fadeIn ${
                currentStep >= 2 ? 'border-indigo-200 shadow-xl' : 'border-gray-100 opacity-90'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    currentStep >= 2 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    2
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Votre domaine d'expertise
                  </h3>
                  <div className="ml-auto text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                    {allCategories[promptType]?.length} domaines
                  </div>
                </div>
                
                {/* Barre de recherche */}
                <div className="mb-4 relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder="Rechercher un domaine..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                  />
                  {categorySearch && (
                    <button
                      onClick={() => setCategorySearch('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      ✕
                    </button>
                  )}
                </div>

                <div className="mb-3 text-sm text-gray-600">
                  {categorySearch ? 
                    `${allCategories[promptType]?.filter(cat => 
                      cat.label.toLowerCase().includes(categorySearch.toLowerCase()) ||
                      cat.description.toLowerCase().includes(categorySearch.toLowerCase())
                    ).length} résultats` : 
                    'Cliquez sur votre domaine ou utilisez la recherche'
                  }
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-h-96 overflow-y-auto p-1 border border-gray-100 rounded-lg">
                  {allCategories[promptType]
                    ?.filter(cat => 
                      categorySearch === '' || 
                      cat.label.toLowerCase().includes(categorySearch.toLowerCase()) ||
                      cat.description.toLowerCase().includes(categorySearch.toLowerCase())
                    )
                    .map((cat) => (
                    <button
                      key={cat.value}
                      onClick={() => {
                        setSelectedCategory(cat.value);
                        setCategorySearch('');
                      }}
                      className={`p-3 rounded-lg border-2 transition-all text-left hover:scale-102 ${
                        selectedCategory === cat.value
                          ? 'border-indigo-500 bg-indigo-50 shadow-md'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className={`font-medium text-sm ${
                        selectedCategory === cat.value ? 'text-indigo-900' : 'text-gray-900'
                      }`}>
                        {cat.label}
                      </div>
                      <div className={`text-xs mt-1 ${
                        selectedCategory === cat.value ? 'text-indigo-600' : 'text-gray-500'
                      }`}>
                        {cat.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Étape 3: Description SIMPLIFIÉE */}
            {selectedCategory && (
              <div className={`bg-white rounded-2xl shadow-lg border p-6 transition-all animate-fadeIn ${
                currentStep >= 3 ? 'border-indigo-200 shadow-xl' : 'border-gray-100 opacity-90'
              }`}>
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
                    currentStep >= 3 ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    3
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Décrivez votre besoin
                  </h3>
                </div>

                {/* Exemples cliquables TOUJOURS VISIBLES */}
                {exampleSuggestions[promptType]?.[selectedCategory] && (
                  <div className="mb-4 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200">
                    <div className="text-sm font-medium text-indigo-900 mb-3 flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Cliquez sur un exemple ou écrivez votre propre besoin :
                    </div>
                    <div className="space-y-2">
                      {exampleSuggestions[promptType][selectedCategory].map((example, idx) => (
                        <button
                          key={idx}
                          onClick={() => applySuggestion(example)}
                          className="w-full text-left p-3 bg-white hover:bg-indigo-50 rounded-lg transition-all border border-indigo-200 hover:border-indigo-300"
                        >
                          <div className="font-medium text-sm text-indigo-900 mb-1">
                            {example.title}
                          </div>
                          <div className="text-sm text-gray-700">
                            {example.text}
                          </div>
                          <div className="text-xs text-gray-500 mt-1 italic">
                            {example.context}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={
                    targetAudience === 'debutant' ?
                      "Décrivez simplement ce que vous voulez accomplir...\n\nExemple : 'J'aimerais créer un site web pour mon restaurant'\n\nN'hésitez pas à donner du contexte : budget, délai, contraintes..." :
                    targetAudience === 'intermediaire' ?
                      "Décrivez votre projet avec quelques détails...\n\nExemple : 'Développer une stratégie marketing digital pour augmenter les ventes de 30% en 6 mois'\n\nPrécisez : contexte, objectifs, ressources disponibles..." :
                      "Détaillez votre challenge stratégique...\n\nExemple : 'Transformation digitale d'une PME industrielle de 200 personnes, budget 500K€, ROI attendu 18 mois'\n\nIncluez : KPIs, contraintes, benchmarks..."
                  }
                  rows={6}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none"
                />
                
                {/* Titre optionnel */}
                {showAdvanced && (
                  <div className="mt-4 p-4 bg-gray-50 rounded-xl animate-fadeIn">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre personnalisé (optionnel)
                    </label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Laissez vide pour génération automatique"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors text-sm"
                    />
                  </div>
                )}

                <button
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="mt-3 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showAdvanced ? 'Masquer' : 'Afficher'} options avancées
                </button>

                {/* Bouton de génération */}
                <button
                  onClick={handleGenerate}
                  disabled={!description.trim() || isGenerating}
                  className="w-full mt-6 bg-gradient-to-r from-indigo-500 to-purple-600 text-white py-4 px-6 rounded-xl font-medium transition-all hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-105"
                >
                  {isGenerating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      <span>Création de votre prompt expert...</span>
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      <span>Générer mon Prompt Expert</span>
                      <ArrowRight className="w-5 h-5" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Results Panel - Plus compact */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sticky top-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <MessageSquare className="w-6 h-6 text-green-600" />
                  <h2 className="text-lg font-semibold text-gray-900">Votre Prompt</h2>
                </div>
                {generatedPrompt && (
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all hover:scale-105"
                  >
                    {copied ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-600" />
                        <span className="text-green-600 text-sm font-medium">Copié !</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 text-gray-600" />
                        <span className="text-gray-600 text-sm font-medium">Copier</span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {generatedPrompt && !editMode ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 max-h-[400px] overflow-y-auto">
                    <div className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                      {formatPromptWithHighlights(generatedPrompt)}
                    </div>
                  </div>
                  
                  {/* Guide de personnalisation */}
                  <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-200">
                    <h3 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Prompt généré avec succès !
                    </h3>
                    <div className="text-sm text-green-700 space-y-2">
                      <p>Votre prompt est prêt à l'emploi !</p>
                      <p className="text-xs">💡 Astuce : Copiez-le directement ou personnalisez-le avec l'assistant.</p>
                    </div>
                  </div>

                  {/* Actions rapides */}
                  <div className="flex gap-2">
                    <button
                      onClick={copyToClipboard}
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {copied ? 'Copié !' : 'Copier'}
                    </button>
                    <button
                      onClick={() => {
                        setGeneratedPrompt('');
                        setDescription('');
                        setTitle('');
                      }}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    >
                      Nouveau
                    </button>
                  </div>

                  {/* Bouton pour l'édition assistée */}
                  {extractBracketFields(generatedPrompt).length > 0 && (
                    <button
                      onClick={startAssistedEditing}
                      className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 text-white py-3 px-4 rounded-xl font-medium transition-all hover:from-purple-600 hover:to-indigo-700 flex items-center justify-center gap-3 shadow-md hover:shadow-lg"
                    >
                      <Sparkles className="w-5 h-5" />
                      <span>Personnaliser avec l'assistant</span>
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ) : editMode ? (
                // Mode édition assistée avec aperçu
                <div className="space-y-4">
                  {/* Aperçu du prompt en cours de personnalisation */}
                  <div className="bg-gray-50 rounded-xl p-3 border border-gray-200 max-h-48 overflow-y-auto">
                    <div className="text-xs font-medium text-gray-600 mb-2">📄 Aperçu en temps réel :</div>
                    <div className="text-gray-800 text-xs leading-relaxed whitespace-pre-wrap font-mono">
                      {formatPromptWithHighlights(getPreviewPrompt(), true)}
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-indigo-50 p-4 rounded-xl border border-purple-200">
                    <h3 className="font-medium text-purple-900 mb-2 flex items-center gap-2">
                      <Sparkles className="w-5 h-5" />
                      Assistant de personnalisation
                    </h3>
                    <div className="text-sm text-purple-700">
                      Remplissez chaque champ • Les modifications apparaissent en vert ci-dessus
                    </div>
                  </div>

                  {/* Progress de l'édition */}
                  <div className="bg-gray-100 rounded-lg p-2">
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-xs text-gray-600">
                        Champ {currentFieldIndex + 1} sur {bracketFields.length}
                      </span>
                      <span className="text-xs text-gray-600">
                        {Math.round(((currentFieldIndex + 1) / bracketFields.length) * 100)}% complété
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-indigo-600 h-2 rounded-full transition-all"
                        style={{ width: `${((currentFieldIndex + 1) / bracketFields.length) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Champ actuel à remplir */}
                  {bracketFields[currentFieldIndex] && (
                    <div className="bg-white p-4 rounded-xl border-2 border-indigo-200 shadow-sm">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Complétez ce champ :
                      </label>
                      <div className="text-lg font-medium text-indigo-900 mb-3">
                        {bracketFields[currentFieldIndex].content}
                      </div>
                      
                      <input
                        type="text"
                        value={fieldValues[bracketFields[currentFieldIndex].fullMatch] || ''}
                        onChange={(e) => setFieldValues({
                          ...fieldValues,
                          [bracketFields[currentFieldIndex].fullMatch]: e.target.value
                        })}
                        placeholder="Tapez votre réponse ici..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                        autoFocus
                      />

                      {/* Suggestions */}
                      {getCurrentFieldSuggestions().length > 0 && (
                        <div className="mt-3">
                          <div className="text-xs text-gray-600 mb-2">
                            💡 Suggestions (cliquez pour utiliser) :
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {getCurrentFieldSuggestions().map((suggestion, idx) => (
                              <button
                                key={idx}
                                onClick={() => setFieldValues({
                                  ...fieldValues,
                                  [bracketFields[currentFieldIndex].fullMatch]: suggestion
                                })}
                                className="px-3 py-1 bg-indigo-100 hover:bg-indigo-200 text-indigo-700 rounded-lg text-sm transition-all"
                              >
                                {suggestion}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Aide contextuelle */}
                      {targetAudience === 'debutant' && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="text-xs text-blue-700 flex items-start gap-2">
                            <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                            <div>
                              <strong>Aide :</strong> 
                              {bracketFields[currentFieldIndex].content.toLowerCase().includes('budget') && 
                                " Indiquez votre budget disponible ou 'À définir' si vous n'êtes pas sûr."}
                              {bracketFields[currentFieldIndex].content.toLowerCase().includes('délai') && 
                                " Précisez le temps dont vous disposez pour ce projet."}
                              {bracketFields[currentFieldIndex].content.toLowerCase().includes('domaine') && 
                                " Décrivez votre expertise principale ou ce que vous faites."}
                              {!bracketFields[currentFieldIndex].content.toLowerCase().includes('budget') && 
                               !bracketFields[currentFieldIndex].content.toLowerCase().includes('délai') &&
                               !bracketFields[currentFieldIndex].content.toLowerCase().includes('domaine') &&
                                " Soyez précis mais concis. Utilisez les suggestions si elles correspondent."}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex gap-2">
                    <button
                      onClick={previousField}
                      disabled={currentFieldIndex === 0}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-100 disabled:text-gray-400 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <ChevronDown className="w-4 h-4 rotate-90" />
                      Précédent
                    </button>
                    
                    {currentFieldIndex < bracketFields.length - 1 ? (
                      <button
                        onClick={nextField}
                        disabled={!fieldValues[bracketFields[currentFieldIndex]?.fullMatch]}
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:text-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                      >
                        Suivant
                        <ChevronDown className="w-4 h-4 -rotate-90" />
                      </button>
                    ) : (
                      <button
                        onClick={finishEditing}
                        disabled={!fieldValues[bracketFields[currentFieldIndex]?.fullMatch]}
                        className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:text-gray-500 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-4 h-4" />
                        Terminer
                      </button>
                    )}
                  </div>

                  {/* Bouton annuler */}
                  <button
                    onClick={() => {
                      setEditMode(false);
                      setFieldValues({});
                      setCurrentFieldIndex(0);
                    }}
                    className="w-full text-gray-500 hover:text-gray-700 text-sm transition-all"
                  >
                    Annuler et revenir au prompt original
                  </button>
                </div>
              ) : finalPrompt ? (
                // Prompt final personnalisé
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-4 rounded-xl border border-green-200">
                    <h3 className="font-medium text-green-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Prompt personnalisé prêt !
                    </h3>
                    <div className="text-sm text-green-700">
                      Tous les champs ont été remplis avec succès
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 max-h-[400px] overflow-y-auto">
                    <div className="text-gray-800 text-sm leading-relaxed whitespace-pre-wrap font-mono">
                      {finalPrompt}
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(finalPrompt);
                        setCopied(true);
                        setTimeout(() => setCopied(false), 2000);
                      }}
                      className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {copied ? 'Copié !' : 'Copier le prompt final'}
                    </button>
                    <button
                      onClick={() => {
                        setEditMode(false);
                        setFinalPrompt('');
                        setFieldValues({});
                        setCurrentFieldIndex(0);
                      }}
                      className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-all"
                    >
                      Retour
                    </button>
                  </div>

                  <button
                    onClick={() => {
                      setGeneratedPrompt('');
                      setDescription('');
                      setTitle('');
                      setEditMode(false);
                      setFinalPrompt('');
                      setFieldValues({});
                      setCurrentFieldIndex(0);
                    }}
                    className="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-lg text-sm font-medium transition-all"
                  >
                    Créer un nouveau prompt
                  </button>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200 text-center">
                  <Wand2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg mb-2">3 étapes simples</p>
                  <p className="text-gray-400 text-sm mb-4">
                    Suivez le guide pour créer votre prompt
                  </p>
                  
                  {/* Mini guide visuel amélioré */}
                  <div className="space-y-3 text-left bg-white p-4 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${currentStep >= 1 ? 'bg-green-500' : 'bg-gray-300'} text-white rounded-full flex items-center justify-center text-sm font-bold transition-all`}>
                        {currentStep > 1 ? '✓' : '1'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700">Choisissez votre objectif</div>
                        <div className="text-xs text-gray-500">Pro ou Créatif</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${currentStep >= 2 ? 'bg-green-500' : 'bg-gray-300'} text-white rounded-full flex items-center justify-center text-sm font-bold transition-all`}>
                        {currentStep > 2 ? '✓' : '2'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700">Sélectionnez votre domaine</div>
                        <div className="text-xs text-gray-500">Parmi {promptType ? allCategories[promptType]?.length : '60+'} options</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 ${currentStep >= 3 ? 'bg-green-500' : 'bg-gray-300'} text-white rounded-full flex items-center justify-center text-sm font-bold transition-all`}>
                        {currentStep > 3 ? '✓' : '3'}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-700">Décrivez votre besoin</div>
                        <div className="text-xs text-gray-500">Ou cliquez sur un exemple</div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer avec ressources */}
        <div className="mt-12 text-center text-sm text-gray-500">
          <p>
            Créé avec ❤️ pour la communauté française du Prompt Engineering
          </p>
          <div className="mt-2 flex items-center justify-center gap-4">
            <span>Niveau : {audienceOptions.find(o => o.value === targetAudience)?.label || '🌱 Débutant'}</span>
            <span>•</span>
            <span>Technique adaptée automatiquement</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }

        .hover\\:scale-102:hover {
          transform: scale(1.02);
        }
      `}</style>
    </div>
  );
};

export default PromptGenerator;